import React, { ReactNode } from "react";
import { Link } from "react-router-dom";
import { MdArrowBackIosNew } from "react-icons/md";
import { AiOutlineLoading } from "react-icons/ai";

/**
 * Shared shell for every "Add ..." page in the dashboard.
 * Provides a uniform window (max-w-6xl), brand-teal accent header,
 * grouped section cards, and a sticky bottom action bar.
 *
 * The functionality, fields, API calls and payload objects of each Add
 * page stay identical — this only standardises the visual structure.
 */

type Pill = { label: string; value?: string | number };

type AddPageShellProps = {
  backRoute: string;
  eyebrow?: string;
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  pills?: Pill[];
  message?: ReactNode;
  modals?: ReactNode;
  sidebar?: ReactNode;
  loading?: boolean;
  submitLabel?: string;
  submitHelper?: string;
  onSubmit?: (e: any) => any;
  onSubmitClick?: () => any;
  headerActions?: ReactNode;
  size?: "md" | "lg" | "xl";
  children?: ReactNode;
};

const widthClass = (size?: "md" | "lg" | "xl") => {
  if (size === "md") return "max-w-4xl";
  if (size === "xl") return "max-w-7xl";
  return "max-w-6xl";
};

export const AddPageShell = ({
  backRoute,
  eyebrow,
  title,
  subtitle,
  icon,
  pills,
  message,
  modals,
  sidebar,
  loading,
  submitLabel = "Save",
  submitHelper = "Review your entries, then submit.",
  onSubmit,
  onSubmitClick,
  headerActions,
  size,
  children,
}: AddPageShellProps) => {
  const max = widthClass(size);

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      {/* Sticky top bar — consistent across all Add/Edit pages */}
      <div className="sticky top-0 z-30 w-full border-b border-gray-100 bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/70">
        <div className={`mx-auto ${max} px-4 py-2.5 sm:px-6 lg:px-8`}>
          <div className="flex items-center justify-between gap-3">
            <Link
              to={backRoute}
              aria-label="Go back"
              className="group inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-gray-800 shadow-sm transition hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-gray-900 text-white shadow-sm transition group-hover:-translate-x-0.5">
                <MdArrowBackIosNew className="text-[11px]" aria-hidden />
              </span>
              <span className="text-[11px] font-semibold tracking-wide">
                Back
              </span>
            </Link>

            {headerActions ? (
              <div className="flex items-center gap-2">{headerActions}</div>
            ) : null}
          </div>
        </div>
      </div>

      {modals}

      <form
        onSubmit={onSubmit}
        className={`mx-auto ${max} px-4 pb-32 pt-6 sm:px-6 lg:px-8`}
      >
        {/* Hero header */}
        <header className="mb-6">
          <div className="relative overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
            {/* Brand accent strip */}
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-brand via-primary to-brand" />
            {/* Soft brand glow */}
            <div
              aria-hidden
              className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-brand/10 blur-3xl"
            />
            <div className="relative flex flex-col gap-4 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-7 sm:py-6">
              <div className="flex min-w-0 items-center gap-4">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-brand to-primary text-white shadow-md ring-1 ring-white/30">
                  {icon ?? <DefaultIcon />}
                </span>
                <div className="min-w-0">
                  {eyebrow ? (
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-brand">
                      {eyebrow}
                    </p>
                  ) : null}
                  <h1 className="mt-0.5 truncate text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl">
                    {title}
                  </h1>
                  {subtitle ? (
                    <p className="mt-1 text-xs text-gray-600 sm:text-sm">
                      {subtitle}
                    </p>
                  ) : null}
                </div>
              </div>

              {pills && pills.length > 0 ? (
                <div className="flex flex-wrap items-center gap-2">
                  {pills.map((p, i) => (
                    <span
                      key={`${p.label}-${i}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-[11px] font-medium text-gray-600 shadow-sm"
                    >
                      <span>{p.label}</span>
                      {p.value !== undefined && p.value !== null ? (
                        <span className="font-semibold text-gray-900 tabular-nums">
                          {String(p.value)}
                        </span>
                      ) : null}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </header>

        {message ? <div className="mb-4">{message}</div> : null}

        {/* Body: optional sidebar + sections */}
        {sidebar ? (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[220px_1fr]">
            <aside className="hidden lg:block">
              <div className="sticky top-20 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
                <p className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500">
                  Sections
                </p>
                <nav className="space-y-1">{sidebar}</nav>
              </div>
            </aside>
            <div className="space-y-5">{children}</div>
          </div>
        ) : (
          <div className="space-y-5">{children}</div>
        )}

        {/* Sticky submit bar */}
        <div className="fixed inset-x-0 bottom-0 z-30 pointer-events-none">
          <div className="mx-auto flex w-fit max-w-[calc(100%-2rem)] justify-center px-4 pb-4 sm:px-6 lg:px-8">
            <div className="pointer-events-auto rounded-2xl border border-gray-200 bg-white/95 px-5 py-3 shadow-2xl backdrop-blur supports-[backdrop-filter]:bg-white/85">
              <div className="flex flex-col items-center justify-center gap-2 sm:flex-row sm:gap-4">
                <p className="hidden text-[11px] text-gray-600 sm:block">
                  {submitHelper}
                </p>
                <SubmitButton
                  loading={loading}
                  label={submitLabel}
                  onClick={onSubmitClick}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────── */

type AddSectionProps = {
  id?: string;
  title: string;
  hint?: string;
  icon?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  contentClass?: string;
};

export const AddSection = ({
  id,
  title,
  hint,
  icon,
  actions,
  children,
  contentClass,
}: AddSectionProps) => {
  return (
    <section id={id} className="scroll-mt-24">
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <header className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
          <div className="flex min-w-0 items-start gap-3">
            {icon ? (
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-brand/10 text-brand">
                {icon}
              </span>
            ) : null}
            <div className="min-w-0">
              <h3 className="truncate text-sm font-semibold tracking-tight text-gray-900">
                {title}
              </h3>
              {hint ? (
                <p className="mt-0.5 text-[11px] text-gray-500">{hint}</p>
              ) : null}
            </div>
          </div>
          {actions ? <div className="shrink-0">{actions}</div> : null}
        </header>
        <div className={contentClass || "px-5 py-5"}>{children}</div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────────────── */

export const AddSectionAnchor = ({
  to,
  children,
}: {
  to: string;
  children: ReactNode;
}) => (
  <a
    href={`#${to}`}
    className="block rounded-lg px-3 py-2 text-[11px] font-semibold text-gray-700 transition hover:bg-gray-50 hover:text-gray-900"
  >
    {children}
  </a>
);

/* ─────────────────────────────────────────────────────────────────── */

const SubmitButton = ({
  loading,
  label,
  onClick,
}: {
  loading?: boolean;
  label: string;
  onClick?: () => any;
}) => {
  const base =
    "relative inline-flex items-center justify-center min-w-fit w-fit overflow-hidden " +
    "rounded-xl bg-brand px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition " +
    "hover:bg-brand/90 hover:shadow-md " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand " +
    "disabled:pointer-events-none disabled:opacity-60";

  return (
    <button
      type="submit"
      disabled={!!loading}
      onClick={onClick}
      className={base}
      aria-busy={loading ? "true" : "false"}
    >
      {loading ? (
        <span className="inline-flex items-center gap-2">
          <AiOutlineLoading className="h-4 w-4 animate-spin" />
          <span>Saving…</span>
        </span>
      ) : (
        <span className="whitespace-nowrap">{label}</span>
      )}
    </button>
  );
};

const DefaultIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
    className="h-5 w-5"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
);
